import { createTutorialAnalytics } from "~/lib/tutorial-analytics"
import { useLocation } from "react-router-dom"
import { driver } from "driver.js"
import { usePostHog } from "posthog-js/react"
import { createElement, useContext, useEffect, useRef, useState } from "react"
import { TutorialContext } from "~/components/tutorials/context"
import { toast } from "sonner"
import { posthogEnabled } from "~/components/providers/Posthog"
import { Button, buttonVariants } from "~/components/ui/button"
import { authClient } from "~/lib/auth-client"
import { tutorials } from "~/lib/tutorials"
import { api } from "~/trpc/react"

// Mount in the owning route/section; attach scope to its containing div.
// Gate automatic on data readiness. start() explicitly replays a seen/hidden tutorial.
export function useTutorial<T extends HTMLElement = HTMLDivElement>(
	id: keyof typeof tutorials,
	{ automatic = true, enabled = true } = {}
) {
	const scope = useRef<T>(null)
	const location = useLocation()
	const { data: session } = authClient.useSession()
	const userId = session?.user.id
	const utils = api.useUtils()
	const { client } = utils
	const posthog = usePostHog()
	const coordinator = useContext(TutorialContext)
	const { ready, allowAutomatic, pending, claim, release, blocked } =
		coordinator
	const [running, setRunning] = useState(false)
	// A tour inside the open settings dialog is not blocked by that dialog.
	const blockedOutsideScope =
		blocked &&
		!scope.current?.closest(
			'[data-slot="dialog-content"], [role="alertdialog"]'
		)
	const automaticallyStartedFor = useRef<string | undefined>(undefined)

	useEffect(() => {
		const requested = pending?.id === id
		if (
			requested &&
			pending.destination &&
			pending.destination !== location.pathname
		)
			return
		const source = requested ? pending.source : "automatic"
		const manual = source === "manual" || source === "announcement"
		const automaticKey = `${userId}:${id}`
		if (
			blockedOutsideScope ||
			!ready ||
			!enabled ||
			!userId ||
			(pending && !requested) ||
			(!requested &&
				(!automatic ||
					!allowAutomatic ||
					automaticallyStartedFor.current === automaticKey))
		)
			return
		const root = scope.current
		if (!root) return
		const modal = document.querySelector(
			'[data-slot="dialog-content"], [role="alertdialog"]'
		)
		if (modal && !modal.contains(root)) return
		const owner = Symbol(id)
		if (!claim(owner)) return
		const analytics = createTutorialAnalytics(id, source)
		let active = true
		let tour: ReturnType<typeof driver> | undefined
		const toastId = `tutorial-dismissed:${userId}:${id}`
		setRunning(true)

		void (async () => {
			try {
				// Always read from the server before an automatic start, not a session
				// snapshot or a browser-local flag that could miss an account opt-out.
				const preferences = await client.user.getTutorialPreferences.query()
				if (!active) return
				if (
					!manual &&
					(preferences.globalHideTutorials || preferences.tutorialHistory[id])
				) {
					setRunning(false)
					release(owner)
					return
				}
				const tutorial = tutorials[id]
				const steps = tutorial.steps.map((step) => ({
					...step,
					element: step.element
						? (root.querySelector(step.element) ?? undefined)
						: undefined
				}))
				// Don't count missing controls as completed or highlight another tab.
				if (
					tutorial.steps.some(
						(step, index) => step.element && !steps[index]?.element
					)
				) {
					setRunning(false)
					release(owner)
					if (manual) toast.error("This tutorial isn't available here yet.")
					return
				}
				tour = driver({
					steps,
					animate: !window.matchMedia("(prefers-reduced-motion: reduce)")
						.matches,
					disableActiveInteraction: true,
					overlayClickBehavior: "none",
					showButtons: ["next", "close"],
					showProgress: true,
					progressText: "{{current}} of {{total}}",
					nextBtnText: "Continue",
					doneBtnText: "Continue",
					closeBtnLabel: "Dismiss",
					popoverClass: "orbit-tutorial",
					stageRadius: 8,
					onHighlighted: (_element, _step, { index }) => {
						if (index !== undefined) analytics.viewStep(index)
					},
					onNextClick: (_element, _step, { driver }) => {
						analytics.finishStep()
						driver.moveNext()
					},
					onPopoverRender: (popover) => {
						// Keep focus inside an owning Radix dialog. A native popover
						// escapes its scroll clipping and transformed containing block.
						const dialog = root.closest('[data-slot="dialog-content"]')
						if (dialog) {
							dialog.append(popover.wrapper)
							popover.wrapper.setAttribute("popover", "manual")
							popover.wrapper.showPopover()
						}
						popover.closeButton.textContent = "Dismiss"
						popover.closeButton.className = `driver-popover-close-btn ${buttonVariants({ variant: "ghost", size: "sm" })}`
						popover.nextButton.className = `driver-popover-next-btn ${buttonVariants({ size: "sm" })}`
						popover.footerButtons.prepend(popover.closeButton)
					},
					onDestroyed: (_element, _step, { reason }) => {
						analytics.end(
							reason === "done"
								? "completed"
								: reason === "close" || reason === "escape"
									? "dismissed"
									: "exited",
							reason ?? "interrupted"
						)
						setRunning(false)
						release(owner)
						if (
							!active ||
							(reason !== "done" && reason !== "close" && reason !== "escape")
						)
							return
						const outcome = reason === "done" ? "completed" : "dismissed"
						void client.user.finishTutorial
							.mutate({ tutorialId: id, outcome })
							.then(() => utils.user.getTutorialPreferences.invalidate())
							.catch(() =>
								toast.error(
									"Couldn't save tutorial progress. It may appear again."
								)
							)
						if (outcome === "dismissed") {
							toast(
								"This tutorial has been dismissed. Want to hide all future tutorials?",
								{
									id: toastId,
									className: "orbit-toast-stacked",
									duration: 20000,
									closeButton: true,
									action: createElement(
										Button,
										{
											variant: "link",
											size: "none",
											onClick: () => {
												toast.dismiss(toastId)
												void client.user.setTutorialPreference
													.mutate({ globalHideTutorials: true })
													.then(() => {
														if (posthogEnabled)
															posthog.capture("tutorials_hidden")
														return utils.user.getTutorialPreferences.invalidate()
													})
													.catch(() =>
														toast.error(
															"Couldn't hide tutorials. Try again in Tutorials settings."
														)
													)
											}
										},
										"Hide all future tutorials"
									)
								}
							)
						}
					}
				})
				automaticallyStartedFor.current = automaticKey
				toast.dismiss(toastId)
				analytics.start()
				tour.drive()
			} catch {
				analytics.end("exited", "error")
				tour?.destroy()
				release(owner)
				if (active) {
					setRunning(false)
					if (manual)
						toast.error("Couldn't load tutorial preferences. Try again.")
				}
			}
		})()

		// Let Driver handle Escape on keyup without closing the owning settings
		// dialog first. Never intercept Escape when no tutorial is active.
		const onKeyDown = (event: KeyboardEvent) => {
			if (tour?.isActive() && event.key === "Escape") event.stopPropagation()
		}
		window.addEventListener("keydown", onKeyDown, true)
		return () => {
			active = false
			analytics.end("exited", "route_or_tab_left")
			window.removeEventListener("keydown", onKeyDown, true)
			tour?.destroy()
			release(owner)
		}
	}, [
		automatic,
		allowAutomatic,
		blockedOutsideScope,
		claim,
		client,
		enabled,
		id,
		pending?.id,
		pending?.source,
		pending?.destination,
		location.pathname,
		posthog,
		ready,
		release,
		userId,
		utils
	])

	useEffect(
		() => () => {
			toast.dismiss(`tutorial-dismissed:${userId}:${id}`)
		},
		[id, userId]
	)

	return { scope, running, start: () => coordinator.start(id) }
}
